/**
 * Client-side QR classifier — mirrors the backend's `classifyScannedQr` in
 * `PhotoScanningService` EXACTLY so the operator never sees a page held back
 * in the local quarantine that the backend would have classified differently
 * on submit.
 *
 * Three outcomes (using the persisted `qr_status` vocabulary so the result can
 * be stored on a quarantine item and rendered with the same badge palette the
 * page grid already uses):
 *
 *   - `success`     — QR looks like `{X}-P{n}` and `X` matches one of the
 *                     registrations the operator is supposed to scan (or no
 *                     expected containers are known, in which case any
 *                     well-formed page-QR is accepted, matching the backend).
 *   - `mismatched`  — QR looks like `{X}-P{n}` but `X` belongs to a different
 *                     container.
 *   - `unverified`  — QR is null / blank / not a page-QR shape at all.
 *
 * The backend uses `match`/`mismatch` internally; the spelling here mirrors the
 * persisted `qr_status` values (`success`/`mismatched`).
 */
export type QrClass = 'success' | 'mismatched' | 'unverified';

/**
 * The container numbers a batch may scan — the backend's own
 * `expected_container_numbers`, i.e. the list its upload classifier uses.
 * Never `containers`: for a batch with no box that is the whole shipment, and
 * classifying against it held correct pages in the quarantine as wrong ones.
 * `container_id` is only a fallback for a payload predating the field; `—` is
 * the resource's no-registration placeholder, not a container.
 */
export function resolveExpectedContainers(
  batch:
    | {
        expected_container_numbers?: string[] | null;
        container_id?: string | null;
        containers?: unknown;
      }
    | null
    | undefined
): string[] {
  if (Array.isArray(batch?.expected_container_numbers)) {
    return batch.expected_container_numbers;
  }
  const own = batch?.container_id;
  return own && own !== '—' ? [own] : [];
}

export function classifyScannedQr(
  qrCode: string | null,
  expectedContainers: string[]
): QrClass {
  if (!qrCode) return 'unverified';
  const m = qrCode.match(/^(.+)-P\d+$/);
  if (!m) return 'unverified';
  if (expectedContainers.length === 0) return 'success';
  return expectedContainers.includes(m[1]) ? 'success' : 'mismatched';
}
